import { logCustomMessage } from '@shared/easyfy.logger';
import { cleanObject } from '@clientShared/utils';
import { globalEvent } from '@shared/easyfy.core';
import { invoke } from '@shared/easyfy.decorators';
import easyfyHttpClient from '@shared/easyfy.httpclient';
@invoke
export class IngridProductWidget {
  private initialized: boolean = false;
  constructor() {
    this.loadIngridScript();

    globalEvent({
      type: 'click',
      events: [
        {
          action: 'init-shipping-details-flyout',
          callback: () => this.initializeWidget(),
        },
      ],
    });
  }

  private async initializeWidget() {
    if (this.initialized) {
      return;
    }
    this.initialized = true;
    const container = document.querySelector<HTMLElement>('[data-container="shipping-details-flyout"]');
    const addToCartBtn = document.querySelector<HTMLElement>('[data-container="addtocart-btn"]');
    const productId = addToCartBtn?.dataset?.searchproductid;
    if (!productId || !container) {
      return;
    }
    const { data: config } = await easyfyHttpClient.get<IngridPDPWidgetConfig>(
      `/api/shipment?handler=ingridpdpwidgetconfig&productId=${encodeURIComponent(productId)}`
    );

    if (!config) {
      logCustomMessage('Ingrid widget: Missing or invalid configuration');
      this.showFallback(container);
      return;
    }

    const cleanedConfig = cleanObject(config);

    try {
      await this.waitForIngridApi();
      this.renderWidget(container, cleanedConfig);
    } catch (error) {
      logCustomMessage('Ingrid widget: Failed to load script', { error: (error as Error)?.message ?? String(error) });
      this.showFallback(container);
    }
  }

  private async loadIngridScript(): Promise<void> {
    if (window._ingridPDPWidgetApi) {
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://cdn.ingrid.com/product-page-widget/bootstrap.js'; // TODO: dynamically set correct staging/live URL
    script.type = 'module';
    script.defer = true;

    document.head.appendChild(script);
  }

  private async waitForIngridApi(maxAttempts: number = 25, intervalMs: number = 100): Promise<void> {
    let attempts = 0;

    while (!window._ingridPDPWidgetApi) {
      attempts++;

      if (attempts >= maxAttempts) {
        throw new Error('Ingrid API failed to load within timeout');
      }

      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
  }

  private renderWidget(container: HTMLElement, config: IngridPDPWidgetConfig) {
    const widgetContainer = container.querySelector<HTMLElement>('#ingrid-product-page-widget');

    if (!widgetContainer) {
      logCustomMessage('Ingrid widget container not found');
      return;
    }

    try {
      window._ingridPDPWidgetApi?.addListener('error', (errorMessage?: string) => {
        logCustomMessage('Ingrid Product Widget Error: ' + errorMessage);
        this.showFallback(container);
      });

      // Render the widget
      window._ingridPDPWidgetApi?.render('ingrid-product-page-widget', config);
    } catch (error) {
      logCustomMessage('Ingrid Product Widget Error: ' + error);
      this.showFallback(container);
    }
  }

  private showFallback(container: HTMLElement) {
    const widgetContainer = container.querySelector<HTMLElement>('#ingrid-product-page-widget');
    const fallbackContainer = container.querySelector<HTMLElement>('#ingrid-fallback');

    if (widgetContainer) {
      widgetContainer.style.display = 'none';
    }

    if (fallbackContainer) {
      fallbackContainer.style.display = 'block';
    }
  }
}

// Ingrid API interfaces
export interface IngridPDPWidgetConfig {
  auth_token: string;
  cart: IngridCart;
  country: string;
  currency: string;
  external_id?: string;
  locales: string[];
  viewed_item: IngridItem;
}

export interface IngridCart {
  attributes?: string[];
  cart_id: string;
  items?: IngridItem[];
  total_value: number;
}

export interface IngridItem {
  /**
   * Attributes of item. They are specific to that particular item and can be set to adjust delivery offering.
   * They can be set in the filter rules for certain shipping categories or carrier products in site configuration
   * so that the delivery offering presented in product page widget is more precise.
   */
  attributes?: string[];
  dimensions?: IngridDimensions;
  /**
   * Discount for the item.
   * @format int32
   */
  discount?: number;
  /** Product name or title. */
  name: string;
  /** Flag indicating if the item is currently out of stock. */
  out_of_stock?: boolean;
  /**
   * Price of the item, defined in minor currency
   * Example `12345` in SEK is 123.45kr and `123` is 1.23kr
   * @format int32
   */
  price: number;
  /**
   * Number of entities of the given item.
   * @format int32
   */
  quantity?: number;
  shipping_date?: IngridShippingDate;
  /** Unique product identifier. */
  sku: string;
  /**
   * Weight of the item in grams.
   * Example `1000` is 1 kg and `10` is 0,01 kg.
   * @format int32
   */
  weight?: number;
}

export interface IngridDimensions {
  /**
   * Height of the item in millimeters.
   * @format int32
   */
  height?: number;
  /**
   * Length of the item in millimeters.
   * @format int32
   */
  length?: number;
  /**
   * Width of the item in millimeters.
   * @format int32
   */
  width?: number;
}

export interface IngridShippingDate {
  /** End of the interval. Date is returned in RFC3339 format. Example `2018-09-08T22:47:31+01:00`. */
  end?: string;
  /** Start of the interval. Date is returned in RFC3339 format. Example `2018-09-08T22:47:3+01:00`.*/
  start?: string;
}

// Global type declaration for Ingrid API
interface IngridPDPWidgetApi {
  render: (containerId: string, config: IngridPDPWidgetConfig) => void;
  addListener: (event: 'ready' | 'error', callback: (data?: string) => void) => void;
  onItemAdded?: (sku: string) => void;
}

declare global {
  interface Window {
    _ingridPDPWidgetApi?: IngridPDPWidgetApi;
  }
}
